import React, { useEffect, useState } from 'react';
import API from '../api';
import CheckoutPage from './CheckoutPage';
import './manualSale.css';

function ManualSale() {
  const [step, setStep] = useState(1);

  const [services, setServices] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');

  const [serviceSearch, setServiceSearch] = useState('');

  const [showCheckout, setShowCheckout] = useState(false);

  const businessId = localStorage.getItem('id');

  // Fetch services
  useEffect(() => {
    if (businessId) {
      API.get(`/auth/services/business/${businessId}`)
        .then(res => setServices(res.data || []))
        .catch(err => {
          console.error('Failed to fetch services', err);
          setServices([]);
        });
    }
  }, [businessId]);

  // Fetch employees based on selected service
  useEffect(() => {
    if (selectedService) {
      API.get('/auth/employees')
        .then(res => {
          const filtered = (res.data || []).filter(emp => {
            return Array.isArray(emp.services_id) &&
              emp.services_id.some(s => {
                const id = typeof s === 'object' ? s?._id || s?.$oid : s;
                return id === selectedService._id;
              });
          });
          setEmployees(filtered);
        })
        .catch(err => {
          console.error('Error fetching employees:', err);
          setEmployees([]);
        });
    } else {
      setEmployees([]);
    }
  }, [selectedService]);

  const handleCashCheckout = () => {
    if (!customerName || !customerPhone || !selectedService || !selectedEmployee) {
      alert('Please fill all required details.');
      return;
    }

    setShowCheckout(true);
  };

  const handleBackFromCheckout = () => {
    setShowCheckout(false);
  };

  // Show CheckoutPage in-place when ready
  if (showCheckout) {
    return (
      <CheckoutPage
        onBack={handleBackFromCheckout}
        payload={{
          bookingId: '000000000000000000000000',
          selectedService,
          selectedEmployee,
          customerDetails: {
            name: customerName,
            phone: customerPhone,
            email: customerEmail,
          },
        }}
      />
    );
  }

  // Real-time filtered services
  const filteredServices = services.filter(s =>
    (s.name || '')
      .toLowerCase()
      .includes(serviceSearch.toLowerCase())
  );

  return (
    <div className="manual-sale-wrapper">
      <h1 className="page-title">Manual Sale</h1>

      <div className="manual-sale-page">
        {/* Step Tabs */}
        <div className="step-indicator">
          {['Service', 'Barber', 'Customer', 'Checkout'].map((label, index) => {
            const stepIndex = index + 1;
            const isActive = step === stepIndex;
            // Tabs are indicative; only allow backward navigation to avoid weird state
            const canClick =
              stepIndex < step ||
              (stepIndex === 1 && !selectedService) ||
              (stepIndex === 2 && selectedService) ||
              (stepIndex === 3 && selectedEmployee);

            return (
              <button
                key={label}
                type="button"
                className={`step-pill ${isActive ? 'active' : ''}`}
                onClick={() => canClick && setStep(stepIndex)}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* SERVICE STEP */}
        {step === 1 && (
          <section>
            <h2 className="section-title">Select Service</h2>

            {/* Search box */}
            <div className="service-search-wrapper">
              <input
                type="text"
                className="service-search-input"
                placeholder="Search service"
                value={serviceSearch}
                onChange={e => setServiceSearch(e.target.value)}
              />
            </div>

            {/* Service grid */}
            <div className="grid">
              {filteredServices.length > 0 ? (
                filteredServices.map(s => (
                  <div
                    key={s._id}
                    className={`card service-card ${
                      selectedService?._id === s._id ? 'selected' : ''
                    }`}
                    onClick={() => setSelectedService(s)}
                  >
                    <div className="card-title">{s.name}</div>
                    <div className="card-price">
                      ₹{Number(s.price || 0).toFixed(0)}
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-results">
                  No services found for “{serviceSearch}”.
                </div>
              )}
            </div>

            <div className="nav-actions">
              <div />
              <button
                type="button"
                className="primary-btn"
                disabled={!selectedService}
                onClick={() => setStep(2)}
              >
                Next
              </button>
            </div>
          </section>
        )}

        {/* BARBER STEP */}
        {step === 2 && (
          <section>
            <h2 className="section-title">Select Barber</h2>

            <div className="grid">
              {employees.length > 0 ? (
                employees.map(emp => (
                  <div
                    key={emp._id}
                    className={`card barber-card ${
                      selectedEmployee?._id === emp._id ? 'selected' : ''
                    }`}
                    onClick={() => setSelectedEmployee(emp)}
                  >
                    <div className="card-title">{emp.name}</div>
                    <div className="card-subtitle">
                      {emp.specialty || 'All Services'}
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-results">
                  No barbers available for the selected service.
                </div>
              )}
            </div>

            <div className="nav-actions">
              <button
                type="button"
                className="secondary-btn"
                onClick={() => setStep(1)}
              >
                Back
              </button>
              <button
                type="button"
                className="primary-btn"
                disabled={!selectedEmployee}
                onClick={() => setStep(3)}
              >
                Next
              </button>
            </div>
          </section>
        )}

        {/* CUSTOMER STEP */}
        {step === 3 && (
          <section>
            <h2 className="section-title">Customer Info</h2>

            <div className="customer-grid">
              <div className="input-group">
                <label>Customer Name</label>
                <input
                  type="text"
                  value={customerName}
                  onChange={e => setCustomerName(e.target.value)}
                  placeholder="Enter customer name"
                />
              </div>
              <div className="input-group">
                <label>Phone Number</label>
                <input
                  type="text"
                  value={customerPhone}
                  onChange={e => setCustomerPhone(e.target.value)}
                  placeholder="Enter phone number"
                />
              </div>
              <div className="input-group">
                <label>Email (optional)</label>
                <input
                  type="email"
                  value={customerEmail}
                  onChange={e => setCustomerEmail(e.target.value)}
                  placeholder="Enter email"
                />
              </div>
            </div>

            <div className="nav-actions">
              <button
                type="button"
                className="secondary-btn"
                onClick={() => setStep(2)}
              >
                Back
              </button>
              <button
                type="button"
                className="primary-btn"
                onClick={handleCashCheckout}
              >
                Proceed Checkout
              </button>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

export default ManualSale;
